import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { createWatchStore, setProperty, updateWatchStore } from 'src/js/store/use-watch';
import { isNilOrEmpty } from 'src/js/util/global-util';

export interface SystemInfo {
  analytics: string;
  audioEncoders: number;
  audioInputs: number;
  capabilities: number;
  COMPorts: number;
  deviceIp: string;
  deviceName: string;
  features: number;
  fqdn: string;
  has2110license: boolean;
  hasEth1: boolean;
  hasHDCP: boolean;
  hasHDR: boolean;
  hasLogo: boolean;
  hasSQD: boolean;
  hasSrtStreamId: boolean;
  hevc: boolean;
  hostname: string;
  licensed: boolean;
  maxStreamContentSources: number;
  nextPersonality: string;
  onvif: boolean;
  personality: string;
  rugged: boolean;
  scte: boolean;
  snapshot: number;
  storage: boolean;
  upgradePending: boolean;
  version: string;
  videoEncoders: number;
  videoInputs: number;
  yuv10bit: boolean;
  yuv422: boolean;
}

// values will be inited from API at runtime
const systemInfo: SystemInfo = {
  analytics: '',
  audioEncoders: 0,
  audioInputs: 0,
  capabilities: 0,
  COMPorts: 0,
  deviceIp: '',
  deviceName: '',
  features: 0,
  fqdn: '',
  has2110license: false,
  hasEth1: false,
  hasHDCP: false,
  hasHDR: false,
  hasLogo: false,
  hasSrtStreamId: false,
  hasSQD: false,
  hevc: false,
  hostname: '',
  licensed: false,
  maxStreamContentSources: 0,
  nextPersonality: '',
  onvif: false,
  personality: 'SDI',
  rugged: false,
  scte: false,
  snapshot: 0,
  storage: false,
  upgradePending: false,
  version: '',
  videoEncoders: 0,
  videoInputs: 0,
  yuv10bit: false,
  yuv422: false,
};

export const isBitSet = (value: number, bit: number) => (value & (1 << bit)) !== 0;

export const systemInfoCapabilities = {
  has2110license: () => systemInfo.has2110license,
  hasAnalytics: () => systemInfo.analytics === 'accepted',
  hasAudioEncoder: () => isBitSet(systemInfo.capabilities, 2),
  hasComPort: () => systemInfo.COMPorts > 0,
  hasCoT: () => isBitSet(systemInfo.features, 1),
  hasHDCP: () => systemInfo.hasHDCP,
  hasHEVCVideoEncoder: () => isBitSet(systemInfo.capabilities, 10),
  hasKLV: () => isBitSet(systemInfo.features, 0),
  hasMetadata: () => isBitSet(systemInfo.features, 0) || isBitSet(systemInfo.features, 1),
  hasSRT: () => isBitSet(systemInfo.features, 2), // SMGR_FEATURE_SRT_STREAMING
  hasVideoEncoder: () => isBitSet(systemInfo.capabilities, 1),
  is2110: () => systemInfo.has2110license && systemInfo.personality == '2110',
  isSQD: () => systemInfo.hasSQD && (systemInfo.personality === 'SQD' || systemInfo.personality === 'DEFENSE'),
  isDVI: () => false,
  isHD: () => isBitSet(systemInfo.capabilities, 5),
  isIPv4: () => isBitSet(systemInfo.capabilities, 11),
  isIPv6: () => isBitSet(systemInfo.capabilities, 12),
  isRugged: () => systemInfo.rugged,
  isSDI: () => isBitSet(systemInfo.capabilities, 7),
};

createWatchStore(systemInfo);

const setSystemInfoProperty = (key: string, value: any) => {
  setProperty(systemInfo, key as keyof typeof systemInfo, value);
};
export { setSystemInfoProperty };
export const saveResponseToSystemInfo = (response: SystemInfo) => {
  if (isNilOrEmpty(response.deviceName)) {
    response.deviceName = response.hostname;
  }
  updateWatchStore(systemInfo, response);
};

interface SystemInfoResponse extends GenericResponse, SystemInfo {}

export const fetchSystemInfo = () => {
  return apiClientWithoutHandler.genericController.get<SystemInfoResponse>(APIPath.systemInfo).then((res) => {
    if (res.ok) {
      const response = res.data as SystemInfo;
      if (response) {
        saveResponseToSystemInfo(response);
      }
    }
    return res;
  });
};

export const updateSystemInfo = (model: Partial<SystemInfo>) => {
  return apiClientWithoutHandler.genericController.put<SystemInfoResponse>(APIPath.systemInfo, model).then((res) => {
    if (res.ok) {
      const response = res.data as SystemInfo;
      if (response) {
        saveResponseToSystemInfo(response);
      }
    }
    return res;
  });
};

export default systemInfo;
